// src/pages/performance/ManageAppraisalsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Tabs,
  Tab,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { CURRENT_YEAR } from "../manageAppraisals.constants";
import { useWorkflowTemplateOptions } from "../manageAppraisals.hooks";
import api from "../../../services/api";
import AppraisalsTab from "../ManageAppraisalsAppraisalsTab";
import QuestionsTab from "../ManageAppraisalsQuestionsTab";

export default function ManageAppraisalsPage() {
  const theme = useTheme();
  const workflowTemplateOptions = useWorkflowTemplateOptions();

  /* ------------------------------------------------------------------ */
  /* Tabs */
  /* ------------------------------------------------------------------ */
  const [tab, setTab] = useState(0);

  /* ------------------------------------------------------------------ */
  /* Global Search (URL sync: ?q=) */
  /* ------------------------------------------------------------------ */
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = useMemo(() => String(searchParams.get("q") || ""), [searchParams]);

  const [searchText, setSearchText] = useState(initialQ);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ);

  useEffect(() => {
    setSearchText(initialQ);
    setDebouncedSearch(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    const q = String(debouncedSearch || "").trim();
    const next = new URLSearchParams(searchParams);

    if (q) next.set("q", q);
    else next.delete("q");

    if (next.toString() !== new URLSearchParams(searchParams).toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, setSearchParams]);

  const clearSearch = useCallback(() => setSearchText(""), []);

  /* ------------------------------------------------------------------ */
  /* Bulk Email Dispatch */
  /* ------------------------------------------------------------------ */
  const [emailDlgOpen, setEmailDlgOpen] = useState(false);
  const [emailScope, setEmailScope] = useState("current_step"); // current_step | all_pending
  const [dryRun, setDryRun] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const openSnack = useCallback((severity, message) => {
    setSnack({ open: true, severity, message });
  }, []);

  const handleOpenEmailDlg = useCallback(() => {
    setLastResult(null);
    setEmailDlgOpen(true);
  }, []);

  const handleCloseEmailDlg = useCallback(() => {
    if (sending) return;
    setEmailDlgOpen(false);
  }, [sending]);

  const handleDispatchEmails = useCallback(async () => {
    setSending(true);
    setLastResult(null);

    try {
      const payload = { scope: emailScope, dry_run: dryRun ? 1 : 0 };
      const test = String(testEmail || "").trim();
      if (test) payload.test_email = test;

      const res = await api.post("/performance/emails/dispatch", payload);
      const data = res?.data;

      setLastResult(data);
      openSnack("success", data?.message || "Emails dispatched successfully.");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to dispatch emails.";
      openSnack("error", msg);
    } finally {
      setSending(false);
    }
  }, [dryRun, emailScope, openSnack, testEmail]);

  /* ------------------------------------------------------------------ */
  /* Styles (simple & clear) */
  /* ------------------------------------------------------------------ */
  const shellBg = useMemo(
    () =>
      theme.hr?.gradient?.page ||
      `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 40%)`,
    [theme]
  );

  return (
    <Box sx={{ minHeight: "100vh", background: shellBg, py: { xs: 1.25, md: 2 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", px: { xs: 1.25, md: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "8px",
            border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
            overflow: "hidden",
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
            boxShadow: "none",
          }}
        >
          {/* ------------------------------------------------------------------ */}
          {/* Header (simple) */}
          {/* ------------------------------------------------------------------ */}
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                  Appraisals
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label="Performance"
                    sx={{
                      fontWeight: 800,
                      borderRadius: "8px",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Year: ${CURRENT_YEAR}`}
                    sx={{
                      fontWeight: 800,
                      borderRadius: "8px",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                    }}
                  />
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      Search applies to both tabs.
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Right controls */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ minWidth: { sm: 520 }, width: { xs: "100%", sm: "auto" } }}
              >
                <TextField
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  placeholder="Search employee name / code..."
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchText ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Tooltip title="Bulk email dispatch">
                  <span>
                    <Button
                      onClick={handleOpenEmailDlg}
                      variant="contained"
                      startIcon={<MarkEmailUnreadOutlinedIcon />}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 900,
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Send Emails
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <Divider />

          {/* ------------------------------------------------------------------ */}
          {/* Tabs */}
          {/* ------------------------------------------------------------------ */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1.5,
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 900,
                minHeight: 48,
              },
            }}
          >
            <Tab label="Appraisals" />
            <Tab label="Question Bank" />
          </Tabs>

          <Divider />

          {/* ------------------------------------------------------------------ */}
          {/* Content */}
          {/* ------------------------------------------------------------------ */}
          <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
            {tab === 0 && (
              <AppraisalsTab
                workflowTemplateOptions={workflowTemplateOptions}
                searchQuery={debouncedSearch}
                onSearchQueryChange={(v) => setSearchText(String(v || ""))}
              />
            )}
            {tab === 1 && <QuestionsTab searchQuery={debouncedSearch} />}
          </Box>
        </Paper>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Bulk Email Dispatch Dialog (simple) */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={emailDlgOpen}
        onClose={handleCloseEmailDlg}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          Bulk Email Dispatch
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
            Runs the backend endpoint <b>/performance/emails/dispatch</b>.
          </Typography>
        </DialogTitle>

        {sending && <LinearProgress />}

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <TextField
              select
              label="Scope"
              value={emailScope}
              onChange={(e) => setEmailScope(e.target.value)}
              size="small"
              disabled={sending}
            >
              <MenuItem value="current_step">Current step (recommended)</MenuItem>
              <MenuItem value="all_pending">All pending steps</MenuItem>
            </TextField>

            <TextField
              label="Test email (optional)"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              size="small"
              disabled={sending}
              placeholder="Leave empty to send to real assignees"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  disabled={sending}
                />
              }
              label="Dry run (no emails sent)"
            />

            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              If backend env overrides recipients (e.g., <b>PERFORMANCE_TEST_EMAIL</b>), emails may go
              only to that inbox.
            </Alert>

            {lastResult && (
              <Alert severity="success" sx={{ borderRadius: "8px" }}>
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: 900 }}>
                    {lastResult?.message || "Done"}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Scope: <b>{lastResult?.scope}</b> — Count: <b>{lastResult?.count}</b>
                  </Typography>
                  {Array.isArray(lastResult?.items) && lastResult.items.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: "8px",
                        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        Preview (first 10):
                      </Typography>
                      {lastResult.items.slice(0, 10).map((it, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: 12.5, mt: 0.6 }}
                        >
                          #{idx + 1} appraisal:{it.appraisal_id} step:{it.step_order} to:{it.to}{" "}
                          {it.status ? `(${it.status})` : ""}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.25 }}>
          <Button onClick={handleCloseEmailDlg} disabled={sending} sx={{ textTransform: "none" }}>
            Close
          </Button>

          <Button
            onClick={handleDispatchEmails}
            variant="contained"
            disabled={sending}
            startIcon={<SendOutlinedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            {dryRun ? "Run Dry Test" : "Send Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
